/**
 * The motion standard's values for code. The curves and durations are custom properties (`motion.css`, loaded with the
 * theme), which a style sheet reads directly; this module is for what cannot: a timer that waits for a transition to end,
 * and the props of a Mantine `Transition`, which take their values as data.
 */

/** The two sizes of change: a small one (a chevron, a row) and a panel sliding. */
export type MotionDuration = 'small' | 'panel';

/** The curves, as the custom property that holds each, for a style written in code. */
export const MOTION_EASE = {
  standard: 'var(--motion-ease-standard)',
  enter: 'var(--motion-ease-enter)',
  exit: 'var(--motion-ease-exit)',
} as const;

/**
 * The durations in milliseconds where the style sheet is not loaded to say (a test, a story), and so what a wait lasts
 * then. `motion.spec.ts` checks them against `motion.css`.
 */
export const MOTION_FALLBACK_MS: Readonly<Record<MotionDuration, number>> = {
  small: 200,
  panel: 300,
};

/** Whether the person has asked for no motion. A browser without `matchMedia` (jsdom) has not. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** A CSS time, `300ms` or `0.2s`, in milliseconds; null for anything else, including an empty value. */
export function parseCssTime(value: string): number | null {
  const match = /^(-?\d*\.?\d+)(ms|s)$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const amount = parseFloat(match[1]);
  return match[2] === 's' ? amount * 1000 : amount;
}

/**
 * How long a transition of this size runs now, in milliseconds. It is read from the custom property the style sheets use,
 * so a wait ends when the transition does, and is nil under reduced motion, where the property is nil. Where the property
 * is not set the fallback stands, and is nil for a person who asked for no motion.
 */
export function motionMs(size: MotionDuration): number {
  if (typeof document !== 'undefined') {
    const read = getComputedStyle(document.documentElement).getPropertyValue(
      `--motion-duration-${size}`
    );
    const parsed = parseCssTime(read);
    if (parsed !== null) {
      return parsed;
    }
  }
  return prefersReducedMotion() ? 0 : MOTION_FALLBACK_MS[size];
}

/**
 * The props of a Mantine `Transition` (a popover's, a menu's, a modal's `transitionProps`) at the standard's pace: a fade
 * over the small duration on the standard curve, and none under reduced motion. Read when the component renders.
 */
export function motionTransition(transition: 'fade' | 'pop' = 'fade'): {
  readonly transition: 'fade' | 'pop';
  readonly duration: number;
  readonly timingFunction: string;
} {
  return {
    transition,
    duration: motionMs('small'),
    timingFunction: MOTION_EASE.standard,
  };
}
